import { useState, useEffect } from "react";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Link2, Copy, Check, Users, Coins } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";

export default function ReferralWidget() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    API.get("/referral").then(r => setData(r.data)).catch(() => {});
  }, []);

  const copyLink = () => {
    const link = `${window.location.origin}/register?ref=${data?.code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Referral link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const applyCode = async () => {
    if (!code.trim()) return;
    setApplying(true);
    try {
      const { data: res } = await API.post("/referral/apply", { code: code.trim() });
      toast.success(res.message);
      setCode("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setApplying(false); }
  };

  if (!data) return null;

  return (
    <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-5 space-y-4" data-testid="referral-widget">
      <div className="flex items-center gap-2 mb-1">
        <Link2 className="w-4 h-4 text-[#B026FF]" />
        <span className="text-sm font-bold text-[#F8FAFC]">Referral Program</span>
      </div>

      {/* Your referral link */}
      <div>
        <p className="text-xs text-[#64748B] mb-2">Share your link — earn 100 coins per friend</p>
        <div className="flex gap-2">
          <div className="flex-1 bg-[#0B0E14] rounded-lg px-3 py-2 border border-[#2A3241] text-xs font-mono-game text-[#94A3B8] truncate">
            {window.location.origin}/register?ref={data.code}
          </div>
          <button onClick={copyLink} data-testid="copy-referral-btn"
            className="px-3 py-2 text-xs font-bold uppercase bg-[#B026FF]/20 text-[#B026FF] border border-[#B026FF]/30 rounded-lg hover:bg-[#B026FF]/30">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-[#0B0E14] rounded-lg p-3 text-center border border-[#2A3241]/30">
          <Users className="w-4 h-4 mx-auto text-[#00F0FF] mb-1" />
          <p className="font-mono-game text-lg font-bold text-[#F8FAFC]">{data.referral_count}</p>
          <p className="text-[10px] text-[#64748B] tracking-wider uppercase">Referrals</p>
        </div>
        <div className="bg-[#0B0E14] rounded-lg p-3 text-center border border-[#2A3241]/30">
          <Coins className="w-4 h-4 mx-auto text-[#FFD600] mb-1" />
          <p className="font-mono-game text-lg font-bold text-[#FFD600]">{data.total_earnings}</p>
          <p className="text-[10px] text-[#64748B] tracking-wider uppercase">Earned</p>
        </div>
      </div>

      {/* Apply a code */}
      <div>
        <p className="text-xs text-[#64748B] mb-2">Have a friend's code? Apply it for +50 coins</p>
        <div className="flex gap-2">
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter referral code"
            className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-9 text-sm" data-testid="referral-code-input" />
          <button onClick={applyCode} disabled={applying} data-testid="apply-referral-btn"
            className="px-4 py-2 text-xs font-bold uppercase bg-[#00F0FF]/20 text-[#00F0FF] border border-[#00F0FF]/30 rounded-lg hover:bg-[#00F0FF]/30 disabled:opacity-50 whitespace-nowrap">
            {applying ? "..." : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}
